import React, { useState, useCallback, useEffect } from 'react';
import { Autocomplete, TextField } from '@mui/material';

interface Option {
  id: string | number;
  [key: string]: any;
}

interface LGAutocompleteProps<T extends Option> {
  fetchFunction: (params: { [key: string]:  string | number | boolean | null }) => Promise<{ data: T[] }>;
  fetchBy?: string  | null;
  optionLabel: string | string[];
  optionSeparator?: string;
  value?: T | null;
  onChange?: (event: React.SyntheticEvent, value: T | null) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  debounceMs?: number;
  queryParams?: { [key: string]: string | number | boolean | null };
}

// Helper function to get nested values using dot notation
const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const LGAutocomplete = <T extends Option>({
  fetchFunction,
  fetchBy = "code",
  optionLabel = "name",
  optionSeparator = " ",
  value,
  onChange,
  label,
  error,
  helperText,
  debounceMs = 300,
  queryParams = {},
  ...props
}: LGAutocompleteProps<T>) => {
  const [options, setOptions] = useState<T[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  const getOptionLabel = (option: T) => {
    if (Array.isArray(optionLabel)) {
      return optionLabel
        .map((label) => {
          if (label.includes(".")) {
            return getNestedValue(option, label) || "";
          }
          return option[label] || "";
        })
        .join(optionSeparator);
    }
    if (typeof optionLabel === "string" && optionLabel.includes(".")) {
      return getNestedValue(option, optionLabel) || option.name || "";
    }
    return String(option[optionLabel] || option.name || "");
  };

  // Debounced fetch function
  const debouncedFetch = useCallback(
    (() => {
      let timeoutId: ReturnType<typeof setTimeout>;
      return (searchTerm: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          setLoading(true);
          try {
            const response = await fetchFunction({ 
              ...(searchTerm.trim() ? { [fetchBy as string]: searchTerm.toUpperCase() } : {}),
              ...queryParams
            });
            
            // Handle different response structures
            let data: T[] | null = null;
            if (response && response.data && Array.isArray(response.data)) {
              data = response.data;
            } else if (Array.isArray(response)) {
              data = response;
            }
            
            if (data && Array.isArray(data)) {
              setOptions(data);
            } else {
              setOptions([]);
            }
          } catch (error) {
            console.error('Error fetching options:', error);
            setOptions([]);
          } finally {
            setLoading(false);
          }
        }, debounceMs);
      };
    })(),
    [debounceMs, fetchBy] // Remove fetchFunction and queryParams from dependencies
  );

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    debouncedFetch(newInputValue);
  };

  // Initial fetch on component mount
  useEffect(() => {
    const initialFetch = async () => {
      setLoading(true);
      try {
        const response = await fetchFunction({ 
          ...queryParams
        });
        
        // Handle different response structures
        let data: T[] | null = null;
        if (response && response.data && Array.isArray(response.data)) {
          data = response.data;
        } else if (Array.isArray(response)) {
          data = response;
        }
        
        if (data && Array.isArray(data)) {
          setOptions(data);
        } else {
          setOptions([]);
        }
      } catch (error) {
        console.error('Error fetching initial options:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    initialFetch();
  }, []); // Remove fetchFunction and queryParams from dependencies

  // Sync display when value is set externally (e.g. edit forms)
  useEffect(() => {
    if (value) {
      setInputValue(getOptionLabel(value));
      setOptions((prev) =>
        prev.some((o) => o.id === value.id) ? prev : [value, ...prev]
      );
    } else {
      setInputValue('');
      setOptions([]);
    }
  }, [value]);

  
  return (
    <Autocomplete<T>
      options={options}
      getOptionLabel={getOptionLabel}
      value={value}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      isOptionEqualToValue={(option, value) =>
        option.id === value.id
      }
      onChange={(event, newValue) => {
        onChange?.(event, newValue);
      }}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          variant="outlined"
        />
      )}
      {...props}
    />
  );
};

export default LGAutocomplete; 